import React, { useCallback, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Box, Button, Card, Container, Grid, Radio, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/lab';
import { format } from 'date-fns';

import { routes } from 'config';
import { LocalizationContext } from 'contexts';
import { ReportType } from 'types/Report';
import { addQuery } from 'helpers/query';
import { useReportTemplates } from 'shared/hooks/useReportTemplates';
import { useQueryState } from 'shared/hooks/useQueryState';
import { BodyLoading } from 'shared/components/layout/BodyLoading/BodyLoading';

export const CreateReportForm = () => {
  const [date] = useQueryState('reportDate');
  const [id] = useQueryState('templateId');
  const { dictionary } = useContext(LocalizationContext);
  const navigate = useNavigate();
  const location = useLocation();

  const { templates, status } = useReportTemplates({
    category: [ReportType.Report],
  });
  
  const methods = useForm<any>({
    defaultValues: {
      templateId: id ? Number(id) : undefined,
      reportDate: date ? new Date(date || '') : null,
    },
  });

  const { handleSubmit, watch, setValue } = methods;

  const reportDate = watch('reportDate');
  const templateId = watch('templateId');

  const onSubmit = useCallback((data: any) => {
    navigate({
      pathname: routes.previewReportTemplate(data.templateId),
      search: addQuery(location.search, {
        reportDate: format(data.reportDate, 'yyyy-MM-dd'),
        templateId: data.templateId,
      }),
    });
  }, [navigate, location]);


  return (
    <Container maxWidth="md">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container direction="column" alignItems="center" sx={{ minHeight: 'calc(100vh - 100px)', mt: '100px' }}>
            <Grid item>
              <Card sx={{ p: 3, position: 'relative', backgroundColor: '#F7F7F7' }}>
                <Grid container>
                  <Grid item sm={10}>
                    <Typography variant="h4">{dictionary.reports.form.createReport}</Typography>
                    <Typography>{dictionary.reports.form.createReportSubtitle}</Typography>
                  </Grid>
                  <Grid item sm={2}>
                    <Controller
                      name="reportDate"
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          inputFormat="dd.MM.yyyy"
                          mask="__.__.____"
                          label={dictionary.reports.form.fieldReportDate}
                          renderInput={props => <TextField size="small" {...props}/>}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} paddingY="30px">
                    {(status === 'loading' || !templates) && (
                      <BodyLoading height="40vh"/>
                    )}
                    <Box sx={{ maxHeight: '350px', overflowY: 'auto' }}>
                      {templates?.map(({ id, title, questions_total }: any, i: number) => (
                        <Box
                          key={id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            border: '1px solid #ECECEC',
                            background: !templateId ? 'white' : parseInt(templateId || '') === id ? 'white' : 'transparent',
                            marginBottom: '10px',
                            cursor: 'pointer',
                          }}
                          onClick={() => setValue('templateId', id)}
                        >
                          <Controller
                            name="templateId"
                            render={({ field }) => (
                              <Radio
                                {...field}
                                value={id}
                                checked={parseInt(templateId || '') === id}
                              />
                            )}
                          />
                          <Typography fontWeight="500">{title}</Typography>
                          <Typography ml="3px">{dictionary.reports.list.questionCount(questions_total)}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item alignItems="center" display="flex" justifyContent="space-between" width="100%" mt="30px">
              <Button
                variant="outlined"
                type="button"
                size="large"
                color="primary"
                onClick={() => navigate(routes.reports)}
              >
                {dictionary.reports.form.backButton}
              </Button>
              <Button
                variant="contained"
                type="submit"
                size="large"
                color="primary"
                disabled={!templateId || !reportDate}
              >
                {dictionary.reports.form.nextButton}
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Container>
  );
};
